//
// DISCLAIMER
//
// Copyright 2019-2026 ArangoDB GmbH, Cologne, Germany
//

import _ from "lodash";
import { Moment } from "moment";
import { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Dropdown, Input, Label, Loader, Menu, Message, Popup, Statistic, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import {
  ListAllOrganizationsRequest as ApiListAllOrganizationsRequest,
  ListOptions as ApiListOptions,
  OrganizationInfo as ApiOrganizationInfo,
  OrganizationInfoList as ApiOrganizationInfoList,
  Tier as ApiTier,
} from "../../api/lib";
import { Routes } from "../../routes";
import {
  ContentSegment,
  CreationDateFilter,
  CreationDateFilters,
  ErrorMessage,
  ListActionView,
  LoaderBoxForTable as LoaderBox,
  Loading,
  PagingButtons,
  defaultPageSizeOptions,
  SecondaryMenu,
  SubID,
  Locked,
  momentDefaultFormat,
  momentNow,
  TextLink,
} from "../../ui/lib";
import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";
import { IWithRefreshProps, withRefresh } from "../../util/WithRefresh";
import { FreeToTryColumnView } from "./FreeToTryView";

// Set by organization-inspector (QualifiedLeadInspector), using its default thresholds.
const qualifiedLeadHint =
  "Set automatically by organization-inspector: at least 15,000 (default currency) invoiced in the last 12 months, on Enterprise tier or on Professional tier with Professional/Enterprise usage in the last 90 days.";

// DeletedFilter selects deleted organizations (soft-deleted, still stored) via is_deleted / is_not_deleted.
export type DeletedFilter = "all" | "active" | "deleted";

// deletedFilterOf reads the deleted filter from a request (e.g. the initial selectedFilters).
export const deletedFilterOf = (req?: ApiListAllOrganizationsRequest): DeletedFilter => (req?.is_deleted ? "deleted" : req?.is_not_deleted ? "active" : "all");

interface IFilterViewArgs {
  onNameChanged: (name: string) => void;

  selectedTierID: string;
  tiers: ApiTier[];
  onTierChanged: (tierID: string) => void;

  selectedCreationDateFilterID: string;
  creationDateFilters: CreationDateFilter[];
  onCreationDateFilterChanged: (filterID: string) => void;

  isQualifiedLead: boolean;
  isNotQualifiedLead: boolean;
  onSetQualification: (qualified: boolean, notQualified: boolean) => void;

  deletedFilter: DeletedFilter;
  onDeletedFilterChanged: (filter: DeletedFilter) => void;

  page: number;
  pageSize: number;
  count: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onPageSizeChange?: (pageSize: number) => void;
}

interface IDropdownOption {
  key: string;
  value: string;
  text: string;
}

const FilterView = ({ ...args }: IFilterViewArgs) => {
  const getOptionText = (options: IDropdownOption[], value: string) => {
    const item = _.find(options, (x) => x.value == value);
    return item && item.text;
  };

  const tierOptions = _.orderBy(args.tiers, "name").map((x, i) => {
    return {
      key: `tier-${x.id || ""}`,
      text: x.name || "",
      value: x.id || "",
    };
  });
  tierOptions.unshift({
    key: "tier-all",
    text: "All",
    value: "all",
  });

  const qualificationOptions = [
    {
      key: "only-qualified",
      text: "Only qualified leads",
      value: "only-qualified",
    },
    {
      key: "only-not-qualified",
      text: "Only not qualified leads",
      value: "only-not-qualified",
    },
    {
      key: "qualification-all",
      text: "All",
      value: "all",
    },
  ];
  const selectedQualification = args.isQualifiedLead ? "only-qualified" : args.isNotQualifiedLead ? "only-not-qualified" : "all";
  const onQualificationChanged = (value: string) => {
    switch (value) {
      case "only-qualified":
        args.onSetQualification(true, false);
        break;
      case "only-not-qualified":
        args.onSetQualification(false, true);
        break;
      default:
        args.onSetQualification(false, false);
        break;
    }
  };

  const deletedOptions = [
    { key: "deleted-all", text: "All", value: "all" },
    { key: "deleted-active", text: "Active only", value: "active" },
    { key: "deleted-deleted", text: "Deleted only", value: "deleted" },
  ];

  const creationDateOptions = _.map(args.creationDateFilters, (x) => {
    return {
      key: `crd-${x.id}`,
      text: x.name,
      value: x.id,
    };
  });

  return (
    <Menu borderless pointing stackable>
      <Menu.Item header>Filter</Menu.Item>
      <Menu.Item>
        <Input icon="search" placeholder="Org Name or ID" name="search" onChange={(e, d) => args.onNameChanged(d.value)} />
      </Menu.Item>
      <Dropdown
        item
        value={args.selectedTierID}
        text={`Tier: ${getOptionText(tierOptions, args.selectedTierID) || "?"}`}
        options={tierOptions}
        onChange={(e, d) => args.onTierChanged(d.value as string)}
      />
      <Dropdown
        item
        scrolling
        value={args.selectedCreationDateFilterID}
        text={`Created: ${getOptionText(creationDateOptions, args.selectedCreationDateFilterID) || "?"}`}
        options={creationDateOptions}
        onChange={(e, d) => args.onCreationDateFilterChanged(d.value as string)}
      />
      <Dropdown
        item
        value={selectedQualification}
        text={`Qualified lead: ${getOptionText(qualificationOptions, selectedQualification) || "?"}`}
        options={qualificationOptions}
        onChange={(e, d) => onQualificationChanged(d.value as string)}
      />
      <Dropdown
        item
        value={args.deletedFilter}
        text={`Deleted: ${getOptionText(deletedOptions, args.deletedFilter) || "?"}`}
        options={deletedOptions}
        onChange={(e, d) => args.onDeletedFilterChanged(d.value as DeletedFilter)}
      />
      <Menu.Item position="right" fitted="vertically">
        <PagingButtons {...args} size="tiny" />
      </Menu.Item>
    </Menu>
  );
};

// Arguments for header view
interface IHeaderView {
  loading: boolean;
  handleSort: (col: string) => void;
  column?: string;
  descending: boolean;
}

const HeaderView = ({ ...args }: IHeaderView) => {
  return (
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell
          sorted={args.column && args.column === "name" ? (args.descending ? "descending" : "ascending") : undefined}
          onClick={args.handleSort("name")}
        >
          Name
        </Table.HeaderCell>
        <Table.HeaderCell
          sorted={args.column && args.column === "description" ? (args.descending ? "descending" : "ascending") : undefined}
          onClick={args.handleSort("description")}
        >
          Description
        </Table.HeaderCell>
        <Table.HeaderCell
          sorted={args.column && args.column === "tier" ? (args.descending ? "descending" : "ascending") : undefined}
          onClick={args.handleSort("tier")}
        >
          Tier
        </Table.HeaderCell>
        <Table.HeaderCell>#Free deployments</Table.HeaderCell>
        <Table.HeaderCell>
          <Popup content={qualifiedLeadHint} on={["hover", "focus"]} trigger={<span tabIndex={0}>Qualified lead</span>} />
        </Table.HeaderCell>
        <Table.HeaderCell
          sorted={args.column && args.column === "created" ? (args.descending ? "descending" : "ascending") : undefined}
          onClick={args.handleSort("created")}
        >
          Created
        </Table.HeaderCell>
        <Table.HeaderCell>
          Actions
          <LoaderBox>
            <Loader size="mini" active={args.loading} inline />
          </LoaderBox>
        </Table.HeaderCell>
      </Table.Row>
    </Table.Header>
  );
};

// Interface describing an organization
interface IRowView {
  item: ApiOrganizationInfo;
  onClickView: () => void;
}

const RowView = ({ ...args }: IRowView) => {
  const org = args.item.organization || {};
  const is_qualified = !!args.item.is_qualified_lead;
  return (
    <Table.Row>
      <Table.Cell>
        <TextLink label={org.name} href={Routes.dashboard_sales_organization_detailsWithId(org.id || "")} />
        {!!org.locked && <Locked />}
        {!!org.is_deleted && (
          <Label size="mini" color="red" basic>
            Deleted
          </Label>
        )}
        <SubID>{org.id}</SubID>
      </Table.Cell>
      <Table.Cell>{org.description}</Table.Cell>
      <Table.Cell>{(org.tier || {}).name}</Table.Cell>
      <Table.Cell>
        <FreeToTryColumnView organization={org} />
      </Table.Cell>
      <Table.Cell>{is_qualified ? "OQL" : "-"}</Table.Cell>
      <Table.Cell>
        <DateTimePopupWithUTCAndLocalTime dateTime={org.created_at} label="Created at" />
      </Table.Cell>
      <Table.Cell textAlign="right" collapsing>
        <div className="table-action-buttons">
          <ListActionView onClick={args.onClickView} />
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

// Interface describing the organization list
interface IListView {
  items: ApiOrganizationInfo[];
  loading: boolean;
  onNameChanged: (name: string) => void;
  page: number;
  pageSize: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onPageSizeChange?: (pageSize: number) => void;
  onClickView: (organizationId: string) => void;
  selectedTierID: string;
  tiers: ApiTier[];
  onTierChanged: (id: string) => void;
  handleSort: (col: string) => void;
  column?: string;
  descending: boolean;
}

const ListView = ({ ...args }: IListView) => (
  <Table striped sortable>
    <HeaderView {...args} />
    <Table.Body>
      {args.items.map((item) => {
        const org = item.organization || {};
        const id = org.id;
        return <RowView {...args} key={id || ""} item={item} onClickView={() => args.onClickView(id || "")} />;
      })}
    </Table.Body>
  </Table>
);

interface ITierStatistics {
  tier_id?: string;
  tier_name?: string;
  count?: number;
}

interface IStatistics {
  count?: number;
  by_tier?: ITierStatistics[];
}

interface IStatisticsViewArgs {
  statistics?: IStatistics;
  filterFrom?: Moment;
  filterTo?: Moment;
  isQualifiedLead: boolean;
  isNotQualifiedLead: boolean;
  deletedFilter: DeletedFilter;
}

const StatisticsView = ({ ...args }: IStatisticsViewArgs) => {
  const st = args.statistics || {};
  const by_tier = st.by_tier || [];
  const filterFrom = args.filterFrom;
  const filterTo = args.filterTo;
  const timeFilter =
    !!filterFrom && !!filterTo
      ? `from ${filterFrom.format(momentDefaultFormat)} to ${filterTo.format(momentDefaultFormat)}`
      : !!filterFrom
      ? `from ${filterFrom.format(momentDefaultFormat)}`
      : !!filterTo
      ? `to ${filterTo.format(momentDefaultFormat)}`
      : "from all time";
  const qualificationFilter = args.isQualifiedLead ? " that are qualified leads" : args.isNotQualifiedLead ? " that are not qualified leads" : "";
  const deletedFilter = args.deletedFilter === "active" ? " (not deleted)" : args.deletedFilter === "deleted" ? " (deleted only)" : "";
  return (
    <div>
      <Message>
        Organizations {timeFilter}
        {qualificationFilter}
        {deletedFilter}.
      </Message>
      <Statistic.Group widths="5" size="tiny">
        <Statistic label="Total" value={st.count || 0} />
        {_.orderBy(by_tier, "tier_name").map((x) => (
          <Statistic key={x.tier_id || ""} label={x.tier_name || ""} value={x.count || 0} />
        ))}
      </Statistic.Group>
    </div>
  );
};

interface IEmptyView {
  page: number;
}

const EmptyView = ({ ...args }: IEmptyView) => <div>{args.page > 0 ? "No more organizations" : "No organizations selected"}</div>;

// Interface describing the organization list view arguments
export interface IOrganizationListViewArgs extends RouteComponentProps {
  loading: boolean;
  organizationInfos?: ApiOrganizationInfoList;
  onNameChanged: (name: string) => void;
  page: number;
  pageSize: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onPageSizeChange?: (pageSize: number) => void;
  onClickView: (organizationId: string) => void;
  selectedTierID: string;
  tiers: ApiTier[];
  onTierChanged: (id: string) => void;
  handleSort: (col: string) => void;
  column?: string;
  descending: boolean;
  selectedCreationDateFilterID: string;
  creationDateFilters: CreationDateFilter[];
  onCreationDateFilterChanged: (filterID: string) => void;
  isQualifiedLead: boolean;
  isNotQualifiedLead: boolean;
  onSetQualification: (qualified: boolean, notQualified: boolean) => void;
  deletedFilter: DeletedFilter;
  onDeletedFilterChanged: (filter: DeletedFilter) => void;

  statistics?: IStatistics;
  filterFrom?: Moment;
  filterTo?: Moment;
  filterView?: boolean;
  // Filter currently selected (request without options and empty values), used in filterView mode.
  filterObject: ApiListAllOrganizationsRequest;
}

export const OrganizationListView = ({ ...args }: IOrganizationListViewArgs) => {
  if (!args.organizationInfos) {
    return <Loading />;
  }
  const isEmpty = !args.organizationInfos.items || args.organizationInfos.items.length === 0;
  const organizationInfos = args.organizationInfos ? args.organizationInfos.items : undefined;
  const count = (organizationInfos || []).length;
  if (args.filterView) {
    // Compact mode used to pick a filter (e.g. the scope of a platform notification rule): filter bar and a short preview.
    return (
      <div>
        <FilterView {...args} count={count} />
        {_.isEmpty(args.filterObject) ? (
          <Message>Select filters to start seeing organizations that match</Message>
        ) : (
          <>
            <Message info size="small">
              <b>{args.statistics?.count ?? 0}</b> organization(s) match the selected filters.
            </Message>
            <ListView {...args} items={organizationInfos || []} />
            {isEmpty && <EmptyView page={args.page} />}
          </>
        )}
      </div>
    );
  }
  return (
    <div>
      <FilterView {...args} count={count} />
      <StatisticsView {...args} />
      <ListView {...args} items={organizationInfos || []} />
      {isEmpty && <EmptyView page={args.page} />}
    </div>
  );
};

// Interface decribing the properties of the organization list component
interface IOrganizationListProps extends IWithRefreshProps, RouteComponentProps {
  onOrganizationSelected: (organizationID: string) => void;
  owned_by_user_id?: string;
  member_of_user_id?: string;
  inline?: boolean;
  // filterView renders only the filter bar and a compact preview; the selected filter is reported via onFilterChange.
  filterView?: boolean;
  // Request fields without a filter control (e.g. notification_id) are passed through unchanged.
  selectedFilters?: ApiListAllOrganizationsRequest;
  onFilterChange?: (filterOptions: ApiListAllOrganizationsRequest) => void;
}

// uiOwnedRequestFields are the ListAllOrganizationsRequest fields built from this list's own state and props.
// Every other field of `selectedFilters` is passed through unchanged by reloadOrganizations.
const uiOwnedRequestFields: (keyof ApiListAllOrganizationsRequest)[] = [
  "options",
  "name",
  "name_like",
  "is_qualified_lead",
  "is_not_qualified_lead",
  "is_deleted",
  "is_not_deleted",
  "owned_by_user_id",
  "member_of_user_id",
  "organization_id",
  "tier_id",
  "created_from",
  "created_to",
  "sort_by_name",
  "sort_by_description",
  "sort_by_tier",
  "sort_by_created",
  "sort_descending",
];

// selectedFilter drops the options, the sorting and every empty value, so an untouched filter bar yields {}.
const selectedFilter = (req: ApiListAllOrganizationsRequest): ApiListAllOrganizationsRequest => {
  const filter = _.pickBy(
    _.omit(req, ["options", "sort_by_name", "sort_by_description", "sort_by_tier", "sort_by_created", "sort_descending"]),
    (v) => v !== undefined && v !== null && v !== "" && v !== false
  ) as ApiListAllOrganizationsRequest;
  if (!filter.name) {
    delete filter.name_like;
  }
  return filter;
};

// Interface decribing the state of the organization list component
interface IOrganizationListState {
  organizationInfos?: ApiOrganizationInfoList;
  nameFilter: string;
  page: number;
  pageSize: number;
  owned_by_user_id?: string;
  member_of_user_id?: string;
  organizationID: string;
  refreshNeeded: boolean;
  selectedTierID: string;
  tiers?: ApiTier[];
  column?: string;
  descending: boolean;
  selectedCreationDateFilterID: string;
  statistics?: IStatistics;
  filterFrom?: Moment;
  filterTo?: Moment;
  isQualifiedLead: boolean;
  isNotQualifiedLead: boolean;
  deletedFilter: DeletedFilter;
  filterObject: ApiListAllOrganizationsRequest;
}

// The component to show organizations as a list.
class OrganizationList extends Component<IOrganizationListProps, IOrganizationListState> {
  state: IOrganizationListState = {
    organizationInfos: undefined,
    nameFilter: "",
    page: 0,
    pageSize: defaultPageSizeOptions[0],
    owned_by_user_id: undefined,
    member_of_user_id: undefined,
    organizationID: "",
    refreshNeeded: false,
    selectedTierID: "all",
    tiers: undefined,
    column: undefined,
    descending: false,
    selectedCreationDateFilterID: "all",
    statistics: undefined,
    filterFrom: undefined,
    filterTo: undefined,
    isQualifiedLead: false,
    isNotQualifiedLead: false,
    // Initialized from selectedFilters, e.g. "Show affected" opens with non-deleted organizations only.
    deletedFilter: deletedFilterOf(this.props.selectedFilters),
    filterObject: {},
  };

  reloadTiers = async () => {
    const req: ApiListOptions = {};
    const list = await apiClients.idashboardClient.ListTiers(req);
    this.setState({ tiers: list.items });
  };

  refreshTiers = () => {
    this.props.refreshNow && this.props.refreshNow(this.reloadTiers);
  };

  reloadOrganizations = async () => {
    const req: ApiListAllOrganizationsRequest = {
      ..._.omit(this.props.selectedFilters || {}, uiOwnedRequestFields),
      name: this.state.nameFilter,
      name_like: true,
      is_qualified_lead: this.state.isQualifiedLead,
      is_not_qualified_lead: this.state.isNotQualifiedLead,
      is_deleted: this.state.deletedFilter === "deleted",
      is_not_deleted: this.state.deletedFilter === "active",
      options: {
        page: this.state.page,
        page_size: this.state.pageSize,
      },
    };
    if (this.props.owned_by_user_id) req.owned_by_user_id = this.props.owned_by_user_id;
    if (this.props.member_of_user_id) req.member_of_user_id = this.props.member_of_user_id;
    if (!_.isEmpty(this.state.organizationID)) {
      req.organization_id = this.state.organizationID;
    }
    if (this.state.selectedTierID != "all") {
      req.tier_id = this.state.selectedTierID;
    }
    let filterFrom: Moment | undefined = undefined;
    let filterTo: Moment | undefined = undefined;
    if (this.state.selectedCreationDateFilterID != "all") {
      const f = CreationDateFilters.find((x) => x.id == this.state.selectedCreationDateFilterID);
      if (f) {
        if (f.filterStart) {
          filterFrom = f.filterStart(momentNow().add(-f.created_from_hours, "hours"));
          req.created_from = filterFrom.toDate();
        }
        if (f.filterEnd) {
          filterTo = f.filterEnd(momentNow().add(-f.created_to_hours, "hours"));
          req.created_to = filterTo.toDate();
        }
      }
    }
    // Add sorting
    if (this.state.column) {
      switch (this.state.column) {
        case "name":
          req.sort_by_name = true;
          break;
        case "description":
          req.sort_by_description = true;
          break;
        case "tier":
          req.sort_by_tier = true;
          break;
        case "created":
          req.sort_by_created = true;
          break;
      }
      if (this.state.descending) {
        req.sort_descending = true;
      }
    }
    const filterObject = selectedFilter(req);
    this.setState({ filterObject }, () => {
      this.props.onFilterChange && this.props.onFilterChange(filterObject);
    });
    const organizationInfos = await apiClients.idashboardClient.ListOrganizationInfos(req);
    const count = await apiClients.idashboardClient.CountOrganizations(req);
    const stats: IStatistics = {
      count: count.count,
      by_tier: [],
    };
    const tiers = this.state.tiers || [];
    if (this.state.selectedTierID == "all") {
      await Promise.all(
        tiers.map(async (t) => {
          const treq = _.clone(req);
          treq.tier_id = t.id;
          const count = await apiClients.idashboardClient.CountOrganizations(treq);
          (stats.by_tier || []).push({
            tier_id: t.id,
            tier_name: t.name,
            count: count.count,
          });
        })
      );
    }

    this.setState({
      organizationInfos: organizationInfos,
      statistics: stats,
      filterFrom: filterFrom,
      filterTo: filterTo,
    });
  };

  refreshOrganizations = () => {
    this.props.refreshNow && this.props.refreshNow(this.reloadOrganizations);
  };

  onClickView = (id: string) => {
    this.props.onOrganizationSelected(id);
  };

  static getDerivedStateFromProps(props: IOrganizationListProps, state: IOrganizationListState) {
    // Store prevId in state so we can compare when props change.
    // Clear out previously-loaded data (so we don't render stale stuff).
    if (props.member_of_user_id !== state.member_of_user_id || props.owned_by_user_id !== state.owned_by_user_id) {
      return {
        organizations: undefined,
        refreshNeeded: true,
        member_of_user_id: props.member_of_user_id,
        owned_by_user_id: props.owned_by_user_id,
      };
    }

    // No state update necessary
    return null;
  }

  componentDidMount() {
    this.refreshOrganizations();
    this.refreshTiers();
  }

  componentDidUpdate() {
    if (this.state.refreshNeeded) {
      this.setState({ refreshNeeded: false }, this.refreshOrganizations);
    }
  }

  // onNameFilterChanged drives the combined "Name or ID" search box: a purely
  // numeric query filters by exact organization id, anything else is a name
  // LIKE filter. One of nameFilter / organizationID is always cleared so the
  // two server filters never AND together.
  onNameFilterChanged = (value: string) => {
    const trimmed = value.trim();
    const isId = /^\d+$/.test(trimmed);
    this.setState(
      {
        nameFilter: isId ? "" : value,
        organizationID: isId ? trimmed : "",
        page: 0,
      },
      this.refreshOrganizations
    );
  };

  onTierChanged = (id: string) => {
    this.setState(
      {
        selectedTierID: id,
        page: 0,
      },
      this.refreshOrganizations
    );
  };

  onCreationDateFilterChanged = (id: string) => {
    this.setState(
      {
        selectedCreationDateFilterID: id,
        page: 0,
      },
      this.refreshOrganizations
    );
  };

  onSetQualification = (qualified: boolean, notQualified: boolean) => {
    this.setState(
      {
        isQualifiedLead: qualified,
        isNotQualifiedLead: notQualified,
        page: 0,
      },
      this.refreshOrganizations
    );
  };

  onDeletedFilterChanged = (deletedFilter: DeletedFilter) => {
    this.setState({ deletedFilter, page: 0 }, this.refreshOrganizations);
  };

  onNextPage = () => {
    this.setState(
      (prev) => ({
        page: prev.page + 1,
      }),
      this.refreshOrganizations
    );
  };

  onPreviousPage = () => {
    this.setState(
      (prev) => ({
        page: prev.page - 1,
      }),
      this.refreshOrganizations
    );
  };

  onPageSizeChange = (pageSize: number) => {
    this.setState({ pageSize, page: 0 }, this.refreshOrganizations);
  };

  handleSort = (col: string) => () => {
    if (this.state.column !== col) {
      this.setState(
        {
          column: col,
        },
        this.refreshOrganizations
      );
      return;
    }

    // the same column was clicked, reverse the sorting order
    this.setState({ descending: !this.state.descending }, this.refreshOrganizations);
  };

  render() {
    const listView = (
      <OrganizationListView
        {...this.props}
        {...this.state}
        onNameChanged={this.onNameFilterChanged}
        onTierChanged={this.onTierChanged}
        onNextPage={this.onNextPage}
        onPreviousPage={this.state.page > 0 ? this.onPreviousPage : undefined}
        onPageSizeChange={this.onPageSizeChange}
        onClickView={this.props.onOrganizationSelected}
        tiers={this.state.tiers || []}
        creationDateFilters={CreationDateFilters}
        onCreationDateFilterChanged={this.onCreationDateFilterChanged}
        onSetQualification={this.onSetQualification}
        onDeletedFilterChanged={this.onDeletedFilterChanged}
        handleSort={this.handleSort}
        column={this.state.column}
      />
    );
    if (!!this.props.inline || !!this.props.filterView) {
      return listView;
    }
    return (
      <ContentSegment>
        <ErrorMessage active={!!this.props.error} onDismiss={this.props.clearError} message={this.props.error} />
        <SecondaryMenu>
          <Menu.Item header>Organizations</Menu.Item>
        </SecondaryMenu>
        {listView}
      </ContentSegment>
    );
  }
}

export default withRefresh()(OrganizationList);
